import React from 'react';

/*
 * Severity scale and chips, shared by the audit pages.
 *
 * COLOURS: taken from the validated status palette (dataviz skill reference), checked against the
 * card surface #0a0f16 — all six clear 3:1 contrast and the adjacent-pair CVD gate. Severity is a
 * STATUS scale, not a categorical one, so the rule that applies is "never colour alone": every chip
 * and every bar segment carries its own count and label (or a title tooltip), and the legend repeats
 * them as text. Do not use these colours for anything that is not a severity.
 */

export const SEVERITIES = [
  { key: 'critical', label: 'Critical', short: 'C', color: '#d03b3b' },
  { key: 'high', label: 'High', short: 'H', color: '#ec835a' },
  { key: 'medium', label: 'Medium', short: 'M', color: '#fab219' },
  { key: 'low', label: 'Low', short: 'L', color: '#3987e5' },
  { key: 'informational', label: 'Informational', short: 'I', color: '#898781' },
  { key: 'not_rated', label: 'Not rated', short: 'NR', color: '#9085e9' },
];

export const SEVERITY_BY_LABEL = SEVERITIES.reduce((acc, s) => {
  acc[s.label.toLowerCase()] = s;
  return acc;
}, {});

export const STATUS_STYLES = {
  Resolved: 'bg-emerald-400/10 text-emerald-300 ring-emerald-400/25',
  Acknowledged: 'bg-sky-400/10 text-sky-300 ring-sky-400/25',
  Open: 'bg-amber-400/10 text-amber-200 ring-amber-400/25',
  'Not recorded': 'bg-white/[0.06] text-[#8792a2] ring-white/10',
};

export const statusClass = (status) =>
  STATUS_STYLES[status] || 'bg-white/[0.06] text-[#8792a2] ring-white/10';

/* Count + label chip. The count and label are the meaning; the dot is decoration. */
export const SeverityChip = ({ sev, count, size = 'sm' }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full bg-white/[0.04] ring-1 ring-white/10 ${
      size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm'
    }`}
  >
    <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: sev.color }} aria-hidden="true" />
    <span className="font-semibold text-[#e6ebf1] tabular-nums">{count}</span>
    <span className="text-[#8792a2]">{sev.label}</span>
  </span>
);

/* Stacked distribution bar. 2px gaps between segments; every segment is also written out as text
 * next to the bar, so the colour never carries the meaning on its own. */
export const SeverityBar = ({ counts, total, className = '', height = 'h-2' }) => {
  const sum = total || SEVERITIES.reduce((a, s) => a + (counts[s.key] || 0), 0);
  if (!sum) return null;
  return (
    <div className={`flex ${height} w-full gap-[2px] overflow-hidden rounded-full bg-white/[0.05] ${className}`}>
      {SEVERITIES.map((s) => {
        const n = counts[s.key] || 0;
        if (!n) return null;
        return (
          <span
            key={s.key}
            className="h-full first:rounded-l-full last:rounded-r-full"
            style={{ width: `${(n / sum) * 100}%`, backgroundColor: s.color }}
            title={`${n} ${s.label}`}
          />
        );
      })}
    </div>
  );
};

export default SEVERITIES;
